import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  ArrowRight,
  BookOpen,
  Brain,
  Trophy,
  User,
  Shield,
  Sparkles,
  UserPlus,
  Send,
  ClipboardCheck,
  FileText,
  Calculator,
  Smartphone,
  Award,
  ChevronDown,
  CheckCircle2,
  KeyRound,
} from "lucide-react";
import { Logo } from "../components/ui/Logo";
import { Footer } from "../components/ui/Footer";
import { ThemeToggle } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { PhoneMockup } from "../components/ui/PhoneMockup";
import { api } from "../lib/api";

const BOT_USERNAME = (import.meta.env.VITE_BOT_USERNAME || "soorgaa_bot").replace(/^@/, "");
const BOT_URL = `https://t.me/${BOT_USERNAME}`;

const STEPS = [
  {
    icon: UserPlus,
    title: "Create your account",
    text: "Enter your phone number. Choose your university and your stream. A stream is your field, for example natural science or social science.",
  },
  {
    icon: Send,
    title: "Connect Telegram",
    text: "The first time, open the Soorgaa bot in Telegram and share your phone contact. This links your Telegram to your account. You do this only once.",
  },
  {
    icon: KeyRound,
    title: "Enter your code",
    text: "The bot sends a 6-digit code to your Telegram. Type it on the website. The code works for 5 minutes.",
  },
  {
    icon: ClipboardCheck,
    title: "Choose a course and study",
    text: "Open a course. Read the module or the chapter notes, take a quiz, then try a past exam.",
  },
];

const FEATURES = [
  { icon: BookOpen, title: "Modules", text: "The course file from your university. Read it online or download the PDF. Some modules need Premium." },
  {
    icon: FileText,
    title: "Chapter notes",
    text: "Short notes for each chapter, prepared from the module. Read them when you want a quick review.",
  },
  {
    icon: Brain,
    title: "Quizzes",
    text: "Questions for each chapter. Answer them, see the correct answer, and find the topics you need to read again.",
  },
  {
    icon: Trophy,
    title: "Past exams",
    text: "Practice with real mid and final exams from Ethiopian universities, listed by course and year, so you can see how exam questions are asked.",
  },
  { icon: Award, title: "COC practice", text: "Practice questions for the COC exam, grouped by semester and stream. They are for practice. They are not the real exam." },
  { icon: Calculator, title: "GPA calculator", text: "Enter your course grades and credit hours to see your semester GPA." },
];

const AUDIENCE = [
  {
    icon: GraduationCap,
    title: "Natural science freshmen",
    points: ["Your common courses and your stream courses are in one place", "Practice with past exams for your courses"],
  },
  {
    icon: BookOpen,
    title: "Social science freshmen",
    points: ["Read notes for your reading-heavy courses", "Use quizzes to practice key terms"],
  },
  {
    icon: Smartphone,
    title: "Students on a phone",
    points: [
      "The site works well on small screens",
      "Add it to your home screen like an app",
      "Content you have already opened can be read again when your connection is weak",
    ],
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: "What is Soorgaa?",
    a: "Soorgaa is a study website for first-year students in Ethiopian universities. It gives you modules, chapter notes, quizzes, past exams and COC practice in one place.",
  },
  { q: "Is it free?", a: "Some content is free. Premium unlocks more. You always see what is included before you pay." },
  {
    q: "How do I create an account?",
    a: "1. Open the sign-up page and enter your phone number. 2. Open the Soorgaa bot in Telegram and share your contact. 3. Come back to the page. 4. Type the 6-digit code from the bot. You need Telegram on the same phone number.",
  },
  {
    q: "I did not get my code. What now?",
    a: "1. Check that Telegram is installed and that your phone number is correct. 2. Open the Soorgaa bot and make sure you have shared your contact. 3. Wait 60 seconds, then ask for a new code. 4. Codes work for 5 minutes, so use the newest one. 5. Still no code? Send a message with the Help button in the bot and we will help you.",
  },
  {
    q: "What is the difference between modules and chapter notes?",
    a: "A module is the full course file from your university. Chapter notes are short notes for one chapter, prepared from the module. Use notes for a quick review.",
  },
  {
    q: "Where do past exams come from?",
    a: "They are real mid and final exams from Ethiopian universities. They are listed by course and year.",
  },
  {
    q: "What is COC?",
    a: "COC is the exam you take after you finish a semester. Universities use it to choose students for departments, so students compete for places. COC practice gives you practice questions, grouped by semester and stream, so you can prepare. These questions are not the real exam, and they do not show you what the real exam will ask.",
  },
  {
    q: "Can I use Soorgaa on my phone?",
    a: "Yes. It works in your phone browser. You can add it to your home screen like an app. Content you have already opened can be read again when your connection is weak.",
  },
  {
    q: "Which courses are available?",
    a: "Common courses and stream courses for first-year students. What you see depends on your university, stream and semester. We keep adding content.",
  },
  {
    q: "What does the Telegram bot do?",
    a: "It sends your sign-in code. You can browse courses and download module files in it, check your profile and Premium status, and send a message to our team. Quizzes, exams and notes are on the website.",
  },
  {
    q: "How is my information used?",
    a: "We use your phone number and Telegram account to sign you in. We use your name, university, stream and semester to show the right courses.",
  },
  {
    q: "How do I get help?",
    a: "Open the Soorgaa bot in Telegram and tap Help. Write your problem and send it. We reply in the same chat.",
  },
];

export function LandingPage() {
  const { isAuthenticated, role, tokens } = useAuth();
  const token = tokens?.accessToken;
  const [profile, setProfile] = useState<{ fullName: string; avatar?: string } | null>(null);

  useEffect(() => {
    if (!token || role !== "student") return;
    api
      .profile(token)
      .then((p) => {
        setProfile({ fullName: p.fullName, avatar: p.avatar });
      })
      .catch(() => {});
  }, [token, role]);

  const isAdmin = role === "admin";
  const firstName = profile?.fullName ? profile.fullName.trim().split(" ")[0] : "";
  const portalPath = isAdmin ? "/admin" : "/student/study";
  const profilePath = isAdmin ? "/admin" : "/student/profile";
  const eyebrow = "text-xs font-semibold tracking-wide uppercase text-accent-foreground";
  const h2 = "font-display text-section font-bold mt-1";
  const wrap = "max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16";
  const cta = isAuthenticated ? (
    <Button to={portalPath} variant="primary" size="md" icon={<ArrowRight size={16} />} iconPosition="right">
      {isAdmin ? "Open admin panel" : "Open Study Hub"}
    </Button>
  ) : (
    <Button to="/auth/register" variant="primary" size="md" icon={<ArrowRight size={16} />} iconPosition="right">
      Create account
    </Button>
  );

  return (
    <div className="min-h-svh flex flex-col bg-background text-foreground">
      {/* Navigation Header */}
      <header className="sticky top-0 z-40 px-3 sm:px-6 pt-3 sm:pt-4">
        <div className="max-w-6xl mx-auto glass-overlay border border-border px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between rounded-lg shadow-xs">
          <Logo />
          <nav aria-label="Page sections" className="hidden md:flex items-center gap-6 text-sm font-semibold text-muted-foreground">
            <a href="#how-it-works" className="hover:text-foreground transition">How it works</a>
            <a href="#features" className="hover:text-foreground transition">What you get</a>
            <a href="#telegram-bot" className="hover:text-foreground transition">Telegram bot</a>
            <a href="#faq" className="hover:text-foreground transition">FAQ</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            {isAuthenticated ? (
              <>
                <Link
                  to={profilePath}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border bg-secondary hover:bg-muted text-foreground text-xs font-semibold transition group"
                  title={isAdmin ? "Administrator" : "My profile"}
                >
                  <div className="w-6 h-6 rounded-md bg-primary/10 text-primary border border-primary/20 flex items-center justify-center text-xs font-bold shrink-0">
                    {isAdmin ? <Shield size={12} /> : profile?.avatar ? profile.avatar : <User size={12} />}
                  </div>
                  <span className="hidden sm:inline font-medium text-xs truncate max-w-[120px]">
                    {isAdmin ? "Administrator" : firstName || "My profile"}
                  </span>
                </Link>
                <Button to={portalPath} variant="primary" size="sm" icon={<ArrowRight size={14} />} iconPosition="right">
                  {isAdmin ? "Open admin panel" : "Open Study Hub"}
                </Button>
              </>
            ) : (
              <>
                <Link
                  to="/auth/login"
                  className="inline-flex min-h-11 items-center text-xs sm:text-sm font-semibold text-muted-foreground hover:text-foreground transition px-2.5"
                >
                  Sign in
                </Link>
                <Button to="/auth/register" variant="primary" size="sm">
                  Create account
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-12 sm:pb-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-xs border border-primary/20 bg-primary/10 text-accent-foreground text-xs font-semibold uppercase tracking-wide mb-5">
              {isAdmin ? (
                <span>Signed in as administrator</span>
              ) : isAuthenticated ? (
                <span className="flex items-center gap-1.5">
                  <Sparkles size={12} />
                  Welcome back{firstName ? `, ${firstName}` : ""}
                </span>
              ) : (
                <span>For first-year university students in Ethiopia</span>
              )}
            </div>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-4">
              Study your freshman courses with confidence
            </h1>
            <p className="text-base lg:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
              {isAdmin
                ? "You are signed in as an administrator. This is the page students see. Use the admin panel to manage content."
                : "Soorgaa gives first-year university students in Ethiopia their modules, chapter notes, quizzes, past exams and COC practice in one place. Open it on your phone, read a chapter, then test yourself."}
            </p>
            {!isAdmin && (
              <p className="text-sm text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8 -mt-5 leading-relaxed">
                COC is the exam you take after a semester. Universities use it to choose students for departments.
              </p>
            )}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              {isAuthenticated ? (
                <>
                  <Button to={portalPath} variant="primary" size="lg" icon={<ArrowRight size={16} />} iconPosition="right" className="w-full sm:w-auto">
                    {isAdmin ? "Open admin panel" : "Open Study Hub"}
                  </Button>
                  {!isAdmin && (
                    <Button to={profilePath} variant="secondary" size="lg" className="w-full sm:w-auto">
                      My profile
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button to="/auth/register" variant="primary" size="lg" icon={<ArrowRight size={16} />} iconPosition="right" className="w-full sm:w-auto">
                    Create account
                  </Button>
                  <Button to="/auth/login" variant="secondary" size="lg" className="w-full sm:w-auto">
                    Sign in
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="bg-accent-soft rounded-container p-4 sm:p-6" aria-hidden="true">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { icon: BookOpen, label: "Modules" },
                { icon: FileText, label: "Chapter notes" },
                { icon: Brain, label: "Quizzes" },
                { icon: Trophy, label: "Past exams" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="bg-card border border-border rounded-tile p-4 sm:p-5 flex flex-col gap-3">
                  <div className="w-10 h-10 rounded-md bg-accent-soft text-primary flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <span className="font-display text-base font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About */}
        <section className="border-t border-border">
          <div className={wrap}>
            <div className="max-w-2xl">
              <p className={eyebrow}>About Soorgaa</p>
              <h2 className={h2}>One place for your first-year courses</h2>
              <div className="mt-3 space-y-2 text-base text-muted-foreground leading-relaxed">
                <p>Soorgaa is a study website for first-year students in Ethiopian universities.</p>
                <p>You find your course modules, chapter notes, quizzes, past exams and COC practice in one place.</p>
                <p>What you see depends on your university, your stream and your semester. We add more content over time.</p>
              </div>
              <div className="mt-6">{cta}</div>
            </div>
          </div>
        </section>

        {/* App preview */}
        <section id="preview" className="border-t border-border scroll-mt-24 overflow-hidden">
          <div className={wrap}>
            <div className="max-w-2xl mb-8 sm:mb-10">
              <p className={eyebrow}>See inside</p>
              <h2 className={h2}>Made to read and practice on your phone</h2>
              <p className="text-base text-muted-foreground mt-2 leading-relaxed">
                Pick up where you stopped, read short notes, and test yourself after each chapter.
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 sm:gap-x-6">
              {[
                { src: "/landing/phone-dashboard.webp", title: "Your dashboard", text: "See your progress and continue reading.", alt: "Soorgaa dashboard showing chapter progress and a continue reading button" },
                { src: "/landing/phone-chapters.webp", title: "Chapters", text: "Open notes or a quiz for any chapter.", alt: "Critical Thinking course with its chapter list" },
                { src: "/landing/phone-notes.webp", title: "Short notes", text: "Easy to read, one idea at a time.", alt: "Chapter notes reader with a topic heading and key points" },
                { src: "/landing/phone-quiz.webp", title: "Quizzes", text: "Get instant feedback on each answer.", alt: "Quiz question with the correct answer and an explanation" },
              ].map(({ src, title, text, alt }, i) => (
                <figure key={title} className={i % 2 === 1 ? "lg:translate-y-8" : ""}>
                  <PhoneMockup src={src} alt={alt} />
                  <figcaption className="mt-4 text-center max-w-[220px] mx-auto">
                    <span className="font-display text-base font-semibold block">{title}</span>
                    <span className="text-sm text-muted-foreground leading-snug block mt-0.5">{text}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="border-t border-border scroll-mt-24">
          <div className={wrap}>
            <div className="max-w-2xl mb-8 sm:mb-10">
              <p className={eyebrow}>How it works</p>
              <h2 className={h2}>Start in four steps</h2>
              <p className="text-base text-muted-foreground mt-2 leading-relaxed">It takes a few minutes. You only need your phone and Telegram.</p>
            </div>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {STEPS.map(({ icon: Icon, title, text }, i) => (
                <li key={title}>
                  <Card className="h-full p-5 rounded-card">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-md bg-accent-soft text-primary flex items-center justify-center shrink-0">
                        <Icon size={20} />
                      </div>
                      <span className="text-xs font-semibold text-muted-foreground">Step {i + 1}</span>
                    </div>
                    <h3 className="text-base font-semibold mb-1.5">{title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                  </Card>
                </li>
              ))}
            </ol>
            <div className="mt-8">{cta}</div>
          </div>
        </section>

        {/* What you get */}
        <section id="features" className="border-t border-border scroll-mt-24">
          <div className={wrap}>
            <div className="max-w-2xl mb-8 sm:mb-10">
              <p className={eyebrow}>What you get</p>
              <h2 className={h2}>Everything for your courses</h2>
              <p className="text-base text-muted-foreground mt-2 leading-relaxed">
                Read first, then test yourself, then practice with real exam questions.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {FEATURES.map(({ icon: Icon, title, text }) => (
                <Card key={title} className="p-5 sm:p-6 rounded-card">
                  <div className="w-10 h-10 rounded-md bg-accent-soft text-primary flex items-center justify-center mb-3.5">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-base font-semibold mb-1.5">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                </Card>
              ))}
            </div>

            <div className="mt-8 max-w-2xl">
              <h3 className="font-display text-lg font-bold">Modules and chapter notes</h3>
              <div className="mt-2 space-y-2 text-base text-muted-foreground leading-relaxed">
                <p>A module is the full course file from your university.</p>
                <p>Chapter notes are shorter. They cover one chapter at a time and are easier to read on a phone.</p>
                <p>Use the module for full detail. Use the notes to review before a quiz or exam.</p>
              </div>
            </div>
            <div className="mt-6">{cta}</div>
          </div>
        </section>

        {/* Who it is for */}
        <section className="border-t border-border">
          <div className={wrap}>
            <div className="max-w-2xl mb-8 sm:mb-10">
              <p className={eyebrow}>Who it is for</p>
              <h2 className={h2}>Made for first-year students</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {AUDIENCE.map(({ icon: Icon, title, points }) => (
                <Card key={title} variant="child" className="p-5 sm:p-6 rounded-card">
                  <div className="w-10 h-10 rounded-md bg-accent-soft text-primary flex items-center justify-center mb-3.5">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-base font-semibold mb-3">{title}</h3>
                  <ul className="space-y-2">
                    {points.map((p) => (
                      <li key={p} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                        <CheckCircle2 size={16} className="text-success shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Telegram bot */}
        <section id="telegram-bot" className="border-t border-border scroll-mt-24">
          <div className={wrap}>
            <div className="max-w-2xl">
              <p className={eyebrow}>Telegram bot</p>
              <h2 className={h2}>Use the Soorgaa bot in Telegram</h2>
              <div className="mt-3 space-y-2 text-base text-muted-foreground leading-relaxed">
                <p>The bot sends your sign-in code. You can also use it to browse your courses and download module files.</p>
                <p>In the bot you can see your profile and your Premium status, and send a message to our team if you need help.</p>
                <p>Quizzes, past exams and chapter notes are on the website. The bot gives you a link to open them.</p>
              </div>
              <a
                href={BOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex min-h-12 items-center justify-center gap-2 rounded-btn bg-primary px-6 text-sm font-semibold text-on-accent transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Send size={16} aria-hidden="true" />
                Open the Soorgaa bot
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </div>
          </div>
        </section>

        {/* Free and Premium + information */}
        <section className="border-t border-border">
          <div className={`${wrap} grid md:grid-cols-2 gap-8`}>
            <div>
              <p className={eyebrow}>Free and Premium</p>
              <h2 className={h2}>What is free and what needs Premium</h2>
              <div className="mt-3 space-y-2 text-base text-muted-foreground leading-relaxed">
                <p>Some content is free. Premium unlocks more courses and features. Premium is bought for a semester.</p>
                <p>You always see what a plan includes before you pay.</p>
              </div>
              <div className="mt-6">
                <Button to={isAuthenticated && !isAdmin ? "/student/premium" : "/auth/register"} variant="secondary" size="md">
                  {isAuthenticated && !isAdmin ? "See Premium" : "Create account"}
                </Button>
              </div>
            </div>
            <div>
              <p className={eyebrow}>Your information</p>
              <h2 className={h2}>What we use and why</h2>
              <div className="mt-3 space-y-2 text-base text-muted-foreground leading-relaxed">
                <p>We use your phone number and your Telegram account to sign you in and to send your code.</p>
                <p>We use your name, university, stream and semester to show the right courses.</p>
                <p>If you have a question about your information, send a message with the Help button in the bot.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="border-t border-border scroll-mt-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <div className="mb-8">
              <p className={eyebrow}>FAQ</p>
              <h2 className={h2}>Questions students ask</h2>
            </div>
            <div className="space-y-3">
              {FAQ.map(({ q, a }) => (
                <details key={q} className="group bg-card border border-border rounded-tile">
                  <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-base font-semibold rounded-tile focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {q}
                    <ChevronDown size={18} className="shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <p className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final call to action */}
        <section className="px-4 sm:px-6 pb-12 sm:pb-16">
          <div className="max-w-6xl mx-auto bg-primary text-on-accent rounded-container px-5 sm:px-12 py-10 sm:py-14 text-center">
            <h2 className="font-display text-section font-bold mb-3">
              {isAdmin ? "Admin" : isAuthenticated ? "Keep studying" : "Ready to start?"}
            </h2>
            <p className="text-base max-w-xl mx-auto mb-7 leading-relaxed">
              {isAdmin
                ? "Open the admin panel to manage content and students."
                : isAuthenticated
                  ? "Go back to your courses and continue where you stopped."
                  : "Create your account, then open your first course."}
            </p>
            <Link
              to={isAuthenticated ? portalPath : "/auth/register"}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-btn bg-background px-6 text-sm font-semibold text-foreground transition hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {isAuthenticated ? (isAdmin ? "Open admin panel" : "Open Study Hub") : "Create account"}
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </section>
      </main>

      <Footer variant="landing" />
    </div>
  );
}
